import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Wallet, DollarSign, Gift, AlertTriangle, RefreshCw } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SyndicationSummary = ({ pools, allocations, isStale, isAdmin, recomputing, onRecompute }) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">Base Pool</CardTitle>
            <Wallet className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-blue-600" data-testid="base-pool">{formatCurrency(pools.base_pool)}</div>
            <p className="text-xs text-blue-500">From payments</p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700">Fees Pool</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-purple-600" data-testid="fees-pool">{formatCurrency(pools.fees_pool)}</div>
            <p className="text-xs text-purple-500">From fee transactions</p>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-amber-700">Extras Pool</CardTitle>
            <Gift className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-amber-600" data-testid="extras-pool">{formatCurrency(pools.extras_pool)}</div>
            <p className="text-xs text-amber-500">PFS-only (above target)</p>
          </CardContent>
        </Card>

        <Card className={isStale ? "border-red-200 bg-red-50" : "border-gray-200"}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className={`text-sm font-medium ${isStale ? 'text-red-700' : 'text-gray-700'}`}>Last Computed</CardTitle>
            {isStale && <AlertTriangle className="h-4 w-4 text-red-600" />}
          </CardHeader>
          <CardContent>
            <div className={`text-sm font-medium ${isStale ? 'text-red-600' : 'text-gray-600'}`} data-testid="last-computed">
              {allocations?.last_computed ? formatDate(allocations.last_computed) : 'Never'}
            </div>
            {isStale && <p className="text-xs text-red-500 mt-1">Data may be outdated</p>}
          </CardContent>
        </Card>
      </div>

      {isStale && isAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Syndication data may be stale</p>
              <p className="text-sm text-amber-600">Recompute to refresh allocations based on current transactions.</p>
            </div>
          </div>
          <Button onClick={onRecompute} disabled={recomputing} variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-100" data-testid="recompute-deal-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />Recompute
          </Button>
        </div>
      )}
    </>
  );
};

export default SyndicationSummary;
